// hooks/useSearchLogic.ts

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobal } from "@/context";
import { filterCombinationsByColorAndSize } from "@/utils";
export const useSearchLogic = ({
  searchQuery,
  selectedCategory,
  updateURL
}) => {
  // States
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMinPrice, setSelectedMinPrice] = useState(null);
  const [selectedMaxPrice, setSelectedMaxPrice] = useState(null);
  const [showOnSaleOnly, setShowOnSaleOnly] = useState(false);
  const [selectedOption, setSelectedOption] = useState("default");
  const [initialized, setInitialized] = useState(false);

  // Applied filters
  const [appliedColorOptions, setAppliedColorOptions] = useState([]);
  const [appliedSizeOptions, setAppliedSizeOptions] = useState([]);
  const [appliedFeatureOptions, setAppliedFeatureOptions] = useState([]);
  const [appliedMinPrice, setAppliedMinPrice] = useState(0);
  const [appliedMaxPrice, setAppliedMaxPrice] = useState(0);
  const [appliedOnSaleOnly, setAppliedOnSaleOnly] = useState(false);

  // Selected filters (UI state)
  const [selectedColorOptions, setSelectedColorOptions] = useState([]);
  const [selectedSizeOptions, setSelectedSizeOptions] = useState([]);
  const [selectedFeatureOptions, setSelectedFeatureOptions] = useState([]);

  // Refs
  const searchTimeoutRef = useRef(null);
  const lastSearchRef = useRef("");
  const isSearchingRef = useRef(false);

  // Global state
  const {
    state: {
      searchResults,
      pagination,
      loadingSearch,
      loadingMore,
      filterData,
      sortOrders
    },
    getSearchData,
    clearSearchData
  } = useGlobal();

  // Create search key
  const createSearchKey = useCallback((query, category, colors, sizes, features, sort, minPrice, maxPrice, onSaleOnly) => {
    return `${query}-${category}-${colors.map(c => c.id).join(",")}-${sizes.map(s => s.id).join(",")}-${features.map(f => f.id).join(",")}-${sort}-${minPrice}-${maxPrice}-${onSaleOnly}`;
  }, []);

  // Core search function - stable, no dependencies on applied filters
  const performSearch = useCallback(async (colors_0, sizes_0, features_0, minPrice_0, maxPrice_0, onSaleOnly_0, immediate = false) => {
    if (!searchQuery || !initialized || isSearchingRef.current) return;
    const searchKey = createSearchKey(searchQuery, selectedCategory, colors_0, sizes_0, features_0, selectedOption, minPrice_0, maxPrice_0, onSaleOnly_0);

    // Skip duplicate searches
    if (lastSearchRef.current === searchKey && !immediate) {
      console.log("Skipping duplicate search:", searchKey);
      return;
    }

    // Cancel previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const executeSearch = async () => {
      if (isSearchingRef.current) return;
      console.log("Performing search with key:", searchKey);
      isSearchingRef.current = true;
      lastSearchRef.current = searchKey;
      try {
        const defaultMinPrice = filterData ? Math.floor(Number(filterData.minPrice || 0)) : 0;
        const defaultMaxPrice = filterData ? Math.floor(Number(filterData.maxPrice || 0)) : 0;
        const searchParams = {
          colors: colors_0.map(option => option.id),
          sizes: sizes_0.map(option_0 => option_0.id),
          features: features_0.map(option_1 => option_1.id),
          minPrice: minPrice_0 !== defaultMinPrice ? minPrice_0 : null,
          maxPrice: maxPrice_0 !== defaultMaxPrice ? maxPrice_0 : null,
          onSaleOnly: onSaleOnly_0,
          sort: selectedOption
        };
        await getSearchData(searchQuery, 1, selectedCategory, searchParams);

        // Update applied filters after successful response
        setAppliedColorOptions(colors_0);
        setAppliedSizeOptions(sizes_0);
        setAppliedFeatureOptions(features_0);
        setAppliedMinPrice(minPrice_0);
        setAppliedMaxPrice(maxPrice_0);
        setAppliedOnSaleOnly(onSaleOnly_0);

        // Update URL after success
        updateURL(colors_0, sizes_0, features_0, selectedCategory, minPrice_0, maxPrice_0, onSaleOnly_0, filterData);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        isSearchingRef.current = false;
      }
    };
    if (immediate) {
      await executeSearch();
    } else {
      searchTimeoutRef.current = setTimeout(executeSearch, 300);
    }
  }, [searchQuery, selectedCategory, selectedOption, getSearchData, createSearchKey, updateURL, initialized, filterData]);

  // Stable function to perform search with current applied filters
  const performSearchWithCurrentFilters = useCallback(async () => {
    if (!initialized || isSearchingRef.current) return;

    // Use current applied filters at the time of call - access directly from state
    await performSearch(appliedColorOptions, appliedSizeOptions, appliedFeatureOptions, appliedMinPrice, appliedMaxPrice, appliedOnSaleOnly, true);
  }, [performSearch, initialized]);

  // Handle apply filters
  const handleApplyFilters = useCallback((colors_1, sizes_1, features_1, minPrice_1, maxPrice_1, onSaleOnly_1) => {
    setCurrentPage(1);
    performSearch(colors_1, sizes_1, features_1, minPrice_1 ?? 0, maxPrice_1 ?? 0, onSaleOnly_1, true);
  }, [performSearch]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (pagination && currentPage < pagination.pagesCount && !loadingMore && searchQuery) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      const defaultMinPrice_0 = filterData ? Math.floor(Number(filterData.minPrice || 0)) : 0;
      const defaultMaxPrice_0 = filterData ? Math.floor(Number(filterData.maxPrice || 0)) : 0;
      getSearchData(searchQuery, nextPage, selectedCategory, {
        colors: appliedColorOptions.map(option_2 => option_2.id),
        sizes: appliedSizeOptions.map(option_3 => option_3.id),
        features: appliedFeatureOptions.map(option_4 => option_4.id),
        minPrice: appliedMinPrice !== defaultMinPrice_0 ? appliedMinPrice : null,
        maxPrice: appliedMaxPrice !== defaultMaxPrice_0 ? appliedMaxPrice : null,
        onSaleOnly: appliedOnSaleOnly,
        sort: selectedOption
      });
    }
  }, [pagination, currentPage, loadingMore, searchQuery, filterData, getSearchData, selectedCategory, selectedOption]);

  // Initialize from URL - SIMPLIFIED, NO SEARCHING HERE
  const initializeFromURL = useCallback(async (filterData_0, urlFilters) => {
    try {
      const {
        colors: colors_2,
        sizes: sizes_2,
        features: features_2,
        minPrice: minPriceFromUrl,
        maxPrice: maxPriceFromUrl,
        onSaleOnly: onSaleParam,
        category: categoryToSet
      } = urlFilters;

      // Set basic values
      const defaultMinPrice_1 = Math.floor(Number(filterData_0.minPrice || 0));
      const defaultMaxPrice_1 = Math.floor(Number(filterData_0.maxPrice || 0));
      setSelectedMinPrice(defaultMinPrice_1);
      setSelectedMaxPrice(defaultMaxPrice_1);
      setShowOnSaleOnly(onSaleParam);
      setSelectedColorOptions(colors_2);
      setSelectedSizeOptions(sizes_2);
      setSelectedFeatureOptions(features_2);

      // Check if there are any filters from URL
      const hasUrlFilters = colors_2.length > 0 || sizes_2.length > 0 || features_2.length > 0 || minPriceFromUrl !== defaultMinPrice_1 || maxPriceFromUrl !== defaultMaxPrice_1 || onSaleParam;
      if (hasUrlFilters) {
        // Set applied filters WITHOUT searching
        setAppliedColorOptions(colors_2);
        setAppliedSizeOptions(sizes_2);
        setAppliedFeatureOptions(features_2);
        setAppliedMinPrice(minPriceFromUrl);
        setAppliedMaxPrice(maxPriceFromUrl);
        setAppliedOnSaleOnly(onSaleParam);

        // Update URL
        updateURL(colors_2, sizes_2, features_2, categoryToSet, minPriceFromUrl, maxPriceFromUrl, onSaleParam, filterData_0);
      } else {
        // No URL filters, set defaults
        setAppliedColorOptions([]);
        setAppliedSizeOptions([]);
        setAppliedFeatureOptions([]);
        setAppliedMinPrice(defaultMinPrice_1);
        setAppliedMaxPrice(defaultMaxPrice_1);
        setAppliedOnSaleOnly(false);

        // Update URL
        updateURL([], [], [], categoryToSet, defaultMinPrice_1, defaultMaxPrice_1, false, filterData_0);
      }

      // Save current search key
      const currentSearchKey = createSearchKey(searchQuery || "", categoryToSet, hasUrlFilters ? colors_2 : [], hasUrlFilters ? sizes_2 : [], hasUrlFilters ? features_2 : [], selectedOption, hasUrlFilters ? minPriceFromUrl : defaultMinPrice_1, hasUrlFilters ? maxPriceFromUrl : defaultMaxPrice_1, hasUrlFilters ? onSaleParam : false);
      lastSearchRef.current = currentSearchKey;
    } catch (error_0) {
      console.error("Initialization error:", error_0);
    }
  }, [searchQuery, selectedOption, createSearchKey, updateURL]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSearchData();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      isSearchingRef.current = false;
    };
  }, [clearSearchData]);

  // Computed values
  const filteredResults = useMemo(() => {
    if (!searchResults) return [];
    const selectedColors = appliedColorOptions.map(option_5 => option_5.label);
    const selectedSizes = appliedSizeOptions.map(option_6 => option_6.label);
    const selectedFeatures = appliedFeatureOptions.map(option_7 => option_7.label);
    return filterCombinationsByColorAndSize(searchResults, selectedColors, selectedSizes, selectedFeatures);
  }, [searchResults, appliedColorOptions, appliedSizeOptions, appliedFeatureOptions]);
  const hasMorePages = Boolean(pagination && currentPage < pagination.pagesCount);
  return {
    // States
    currentPage,
    setCurrentPage,
    selectedMinPrice,
    setSelectedMinPrice,
    selectedMaxPrice,
    setSelectedMaxPrice,
    showOnSaleOnly,
    setShowOnSaleOnly,
    selectedOption,
    setSelectedOption,
    initialized,
    setInitialized,
    // Applied filters
    appliedColorOptions,
    appliedSizeOptions,
    appliedFeatureOptions,
    appliedMinPrice,
    appliedMaxPrice,
    appliedOnSaleOnly,
    // Selected filters
    selectedColorOptions,
    selectedSizeOptions,
    selectedFeatureOptions,
    setSelectedColorOptions,
    setSelectedSizeOptions,
    setSelectedFeatureOptions,
    // Global state
    searchResults,
    pagination,
    loadingSearch,
    loadingMore,
    filterData,
    sortOrders,
    // Functions
    handleApplyFilters,
    handleLoadMore,
    initializeFromURL,
    performSearchWithCurrentFilters,
    // Computed
    filteredResults,
    hasMorePages
  };
};